// src/pages/TonKhoPage.js
import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase';
import { collection, onSnapshot, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, writeBatch } from 'firebase/firestore';
import {
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button,
  Box, Typography, IconButton, TextField, Stack, TableSortLabel, TablePagination
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import ProductDialog from '../components/ProductDialog';
import AnalysisDialog from '../components/AnalysisDialog';
import { useAuth } from '../context/AuthContext';
import * as XLSX from 'xlsx';

// --- Helper Functions ---
const formatDate = (timestamp) => {
  if (!timestamp) return 'N/A';
  return new Date(timestamp.seconds * 1000).toLocaleDateString('vi-VN');
};
const formatCurrency = (number) => {
  if (typeof number !== 'number') return 'N/A';
  return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(number);
};
function descendingComparator(a, b, orderBy) {
  const av = a?.[orderBy] ?? '';
  const bv = b?.[orderBy] ?? '';
  if (bv < av) return -1;
  if (bv > av) return 1;
  return 0;
}
function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function TonKhoPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const { currentUser } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'products'), (snapshot) => {
      setProducts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const processedProducts = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const filtered = products.filter(p => {
      const f = (v) => (v ?? '').toString().toLowerCase().includes(term);
      return (
        f(p.name) ||
        f(p.sku) ||
        f(p.note) ||
        f(p.chemicalName) ||         // Tên hóa học
        f(p.casNumber) ||            // Mã số CAS
        f(p.chemicalFormula)         // Công thức hóa học
      );
    });
    return stableSort(filtered, getComparator(order, orderBy));
  }, [products, searchTerm, order, orderBy]);

  if (loading) return <Typography>Đang tải dữ liệu...</Typography>;

  const handleChangePage = (event, newPage) => { setPage(newPage); };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleAdd = () => {
    setCurrentProduct(null);
    setDialogOpen(true);
  };

  const handleEdit = (product) => {
    setCurrentProduct(product);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bạn có chắc chắn muốn xóa sản phẩm này?')) {
      try {
        await deleteDoc(doc(db, 'products', id));
      } catch (error) { console.error("Lỗi khi xóa sản phẩm:", error); }
    }
  };

  const handleSave = async (productData) => {
    const userEmail = currentUser ? currentUser.email : 'unknown';
    try {
      if (currentProduct) {
        await updateDoc(doc(db, 'products', currentProduct.id), {
          ...productData,
          updatedAt: serverTimestamp(),
          updatedBy: userEmail,
        });
      } else {
        await addDoc(collection(db, 'products'), {
          ...productData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          createdBy: userEmail,
          updatedBy: userEmail,
        });
      }
    } catch (error) {
      console.error("Lỗi khi lưu sản phẩm:", error);
    }
    setDialogOpen(false);
  };

  const handleExportExcel = () => {
    const dataToExport = products.map(p => ({
      "Mã SP (sku)": p.sku,
      "Tên SP (name)": p.name,
      "Tên hóa học (chemicalName)": p.chemicalName || '',
      "Mã số CAS (casNumber)": p.casNumber || '',
      "Công thức hóa học (chemicalFormula)": p.chemicalFormula || '',
      "Chú thích (note)": p.note || '',
      "Đơn vị (unit)": p.unit || '',
      "Số lượng (quantity)": p.quantity ?? 0,
      "Đơn giá (price)": p.price ?? 0,
    }));
    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "TonKho");
    XLSX.writeFile(wb, `Du_Lieu_Ton_Kho_${new Date().toLocaleDateString('vi-VN').replace(/\//g, '-')}.xlsx`);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json = XLSX.utils.sheet_to_json(worksheet);
        if (json.length === 0) return alert("File Excel rỗng.");

        const skuToIdMap = new Map(products.map(p => [String(p.sku), p.id]));
        const batch = writeBatch(db);
        const userEmail = currentUser ? currentUser.email : 'unknown';
        let newCount = 0, updateCount = 0;

        json.forEach((row) => {
          const sku = row.sku || row['Mã SP (sku)']; if (!sku) return;

          const dataPayload = {
            sku: sku,
            name: row.name || row['Tên SP (name)'] || 'Không có tên',
            chemicalName: row.chemicalName || row['Tên hóa học (chemicalName)'] || row['Tên hóa học'] || '',
            casNumber: row.casNumber || row['Mã số CAS (casNumber)'] || row['Mã số CAS'] || '',
            chemicalFormula: row.chemicalFormula || row['Công thức hóa học (chemicalFormula)'] || row['Công thức hóa học'] || '',
            note: row.note || row['Chú thích (note)'] || '',
            unit: row.unit || row['Đơn vị (unit)'] || '',
            quantity: Number(row.quantity ?? row['Số lượng (quantity)'] ?? 0),
            price: Number(row.price ?? row['Đơn giá (price)'] ?? 0),
            updatedAt: serverTimestamp(),
            updatedBy: userEmail,
          };

          if (skuToIdMap.has(String(sku))) {
            batch.update(doc(db, 'products', skuToIdMap.get(String(sku))), dataPayload);
            updateCount++;
          } else {
            batch.set(doc(collection(db, 'products')), {
              ...dataPayload,
              createdAt: serverTimestamp(),
              createdBy: userEmail,
            });
            newCount++;
          }
        });

        await batch.commit();
        alert(`Hoàn tất! Đã cập nhật ${updateCount} sản phẩm và thêm mới ${newCount} sản phẩm.`);
      } catch (error) {
        console.error("Lỗi khi import file Excel: ", error);
        alert("Đã xảy ra lỗi khi import file.");
      }
    };
    reader.readAsArrayBuffer(file); e.target.value = null;
  };

  const headCells = [
    { id: 'sku', label: 'Mã SP' },
    { id: 'name', label: 'Tên SP' },
    { id: 'chemicalName', label: 'Tên hóa học' },        // MỚI
    { id: 'casNumber', label: 'Mã số CAS' },             // MỚI
    { id: 'chemicalFormula', label: 'Công thức hóa học' }, // MỚI
    { id: 'note', label: 'Chú thích' },
    { id: 'unit', label: 'Đơn vị' },
    { id: 'quantity', numeric: true, label: 'Tồn kho' },
    { id: 'price', numeric: true, label: 'Đơn giá' },
    { id: 'updatedAt', label: 'Ngày cập nhật' },
    { id: 'updatedBy', label: 'Người thực hiện' },
  ];

  // Permission check
  const isSuperUser = currentUser?.roleNames?.some(name => name.toLowerCase() === 'admin') || currentUser?.permissions?.full_access;
  const canEditInventory = isSuperUser || currentUser?.permissions?.editInventory;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" component="h1">Quản Lý Tồn Kho</Typography>
        {canEditInventory &&
          <Button variant="contained" onClick={handleAdd}>Thêm Sản Phẩm</Button>
        }
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <TextField
            label="Tìm kiếm sản phẩm..."
            variant="outlined"
            size="small"
            sx={{ flexGrow: 1, minWidth: '250px' }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'nowrap' }}>
            <Button variant="outlined" startIcon={<AnalyticsIcon color="primary" />} onClick={() => setAnalysisOpen(true)} sx={{ whiteSpace: 'nowrap' }}>Phân Tích</Button>
            <Button variant="outlined" startIcon={<DownloadIcon color="success" />} onClick={handleExportExcel} sx={{ whiteSpace: 'nowrap' }}>Xuất Excel</Button>
            {canEditInventory &&
              <Button variant="outlined" startIcon={<UploadIcon />} component="label" sx={{ whiteSpace: 'nowrap' }}>
                Import Excel
                <input type="file" hidden accept=".xlsx, .xls" onChange={handleFileUpload} />
              </Button>
            }
          </Stack>
        </Box>
      </Paper>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {headCells.map((headCell) => (
                  <TableCell key={headCell.id} sortDirection={orderBy === headCell.id ? order : false}>
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : 'asc'}
                      onClick={() => handleRequestSort(headCell.id)}
                    >
                      {headCell.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                {canEditInventory && <TableCell align="right">Thao tác</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {processedProducts
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((product) => (
                  <TableRow key={product.id} hover>
                    <TableCell>{product.sku}</TableCell>
                    <TableCell>{product.name}</TableCell>
                    <TableCell>{product.chemicalName || ''}</TableCell>
                    <TableCell>{product.casNumber || ''}</TableCell>
                    <TableCell>{product.chemicalFormula || ''}</TableCell>
                    <TableCell>{product.note}</TableCell>
                    <TableCell>{product.unit}</TableCell>
                    <TableCell align="right">{product.quantity}</TableCell>
                    <TableCell align="right">{formatCurrency(product.price)}</TableCell>
                    <TableCell>{formatDate(product.updatedAt)}</TableCell>
                    <TableCell>{product.updatedBy}</TableCell>
                    {canEditInventory &&
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleEdit(product)}><EditIcon color="warning" /></IconButton>
                        <IconButton size="small" onClick={() => handleDelete(product.id)}><DeleteIcon color="error" /></IconButton>
                      </TableCell>
                    }
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={processedProducts.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Số dòng mỗi trang:"
        />
      </Paper>

      <ProductDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
        initialProduct={currentProduct}
      />
      <AnalysisDialog
        open={analysisOpen}
        onClose={() => setAnalysisOpen(false)}
        products={products}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
    </Box>
  );
}

export default TonKhoPage;
